import { useState } from 'react'
import { NavLink, Outlet } from 'react-router'

import { Avatar } from '../components/Avatar.js'
import { AppHeader } from './AppHeader.js'
import { BarTitleProvider } from './BarTitleProvider.js'
import { LegendSlotContext } from '../components/legend-slot.js'
import { useDisplayScale } from './useDisplayScale.js'
import { useFrameScroll } from './useFrameScroll.js'
import { SoundProvider } from '../sound/SoundProvider.js'
import { useSession } from '../auth/session-context.js'

/** The ordinary chrome: a header with the app name and who is playing. */
export function AppLayout() {
  const { status, user } = useSession()
  const scale = useDisplayScale()

  /*
   * The bottom row, and the element a game's key legend is drawn into on the
   * television. State rather than a ref, because a portal needs the node to
   * exist before it can be given one — a ref would be null on the first render
   * and nothing would re-render to notice it had filled in.
   */
  const [legendSlot, setLegendSlot] = useState<HTMLElement | null>(null)

  /*
   * The scroll region, held the same way and for the same reason: the hook
   * below needs the node itself, and a ref would be null on the first render
   * with nothing to re-render and notice it had filled in.
   */
  const [frame, setFrame] = useState<HTMLElement | null>(null)

  useFrameScroll(frame, scale === 'tv')

  return (
    // Wrapped out here rather than around `<Outlet />`: the bar has to be able
    // to read what it is called, and the bar is inside.
    <BarTitleProvider>
      <div
        /*
         * At least a viewport tall, and the page scrolls if it has to — except
         * on the television, where the shell is exactly a viewport and cannot
         * scroll at all.
         *
         * A fixed shell was tried once before and taken out again, on the
         * grounds that "a box that scrolls within a page that does not is worse
         * than the page scrolling — the wheel only works over the right element
         * and there is no scrollbar where anybody looks for one". Both halves of
         * that were right, and both are now answered rather than ignored:
         *
         * - There is exactly one scroll region per screen and it is `main`,
         *   which at this scale is nearly the whole display rather than a box in
         *   the middle of it. `useFrameScroll` takes the wheel at the window and
         *   forwards it there wherever the pointer is resting, so the pointer
         *   stops mattering — and the keys work too, because a region only a
         *   wheel can reach is one broken wheel away from unreachable content.
         * - `main` is full width at this scale, so its scrollbar is at the right
         *   edge of the screen, which is where anybody looks for one.
         *
         * What has not changed is the order of preference. Fitting the screen is
         * still worth doing by making the content smaller: the clue list shrinks
         * and columns before any of this engages, and every other game fits
         * outright. This is the backstop for a riddle with fourteen clues, which
         * no font size fits, and for a roster longer than the screen.
         *
         * The bargain it makes: overflow that used to run off the bottom of a
         * scrolling page is now *clipped* by the frame, which is silent. What
         * pays for it is the test that nothing outside the scroll region
         * overflows its own box, on every screen.
         */
        className={`flex flex-col ${scale === 'tv' ? 'h-dvh overflow-hidden' : 'min-h-dvh'}`}
        data-scale={scale === 'default' ? undefined : scale}
      >
        <AppHeader>
          {/*
              The grown-up's two screens.
              
              Both existed and neither was linked, so the only way to add an
              account was to know to type /accounts — which is a feature nobody
              can use. Shown only to a parent: a child does not need a door she
              cannot open, and the server refuses her either way.
            */}
          {status === 'authenticated' && user?.role === 'parent' ? (
            <>
              <NavLink
                to="/progress"
                className="min-h-(--size-tap) flex items-center text-(length:--text-small)"
              >
                Progress
              </NavLink>
              <NavLink
                to="/accounts"
                className="min-h-(--size-tap) flex items-center text-(length:--text-small)"
              >
                Accounts
              </NavLink>
            </>
          ) : null}

          {status === 'authenticated' && user ? (
            <NavLink
              to="/profile"
              className="flex min-h-(--size-tap) items-center gap-2 no-underline"
            >
              <Avatar teddy={user.avatarKey} name={user.displayName} size="small" />
              <span className="hidden sm:inline">{user.displayName}</span>
            </NavLink>
          ) : status === 'anonymous' ? (
            <NavLink to="/login" className="flex min-h-(--size-tap) items-center">
              Log in
            </NavLink>
          ) : null}
        </AppHeader>

        {/*
        The one thing on the screen that scrolls, and only on the television.
        `scrollbar-gutter` keeps the space for the bar reserved, so a screen
        that grows past the frame does not shift sideways as the bar appears.
      */}
        <main
          ref={setFrame}
          className="mx-auto flex min-h-0 w-full max-w-5xl [[data-scale=tv]_&]:max-w-none [[data-scale=tv]_&]:overflow-y-auto [[data-scale=tv]_&]:[scrollbar-gutter:stable] flex-1 flex-col p-4 sm:p-6"
        >
          <LegendSlotContext.Provider value={legendSlot}>
            {/*
            Only the television makes a noise. Everywhere else the provider
            builds a player that does nothing at all — no audio element, no
            fetch, no decode — so a phone spends nothing on eight tracks it
            will never play.
          */}
            <SoundProvider heard={scale === 'tv'}>
              <Outlet />
            </SoundProvider>
          </LegendSlotContext.Provider>
        </main>

        {/*
        The television's key legend lands here, below everything else and
        outside the column the games are laid out in — so it is full width, at
        the bottom of the screen, and takes exactly the height its contents
        need rather than a height somebody guessed. Empty at every other scale.
      */}
        <div ref={setLegendSlot} />
      </div>
    </BarTitleProvider>
  )
}
